import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Tv, Cast, Pause, Play, X, Smartphone, QrCode } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera as QrCamera } from "@/pages/ScanPage";

const tvUrl = (code) => `${window.location.origin}/tv/${code}`;
const pairCode = (t) => (String(t || "").match(/lidh-tv\/([A-Za-z0-9]{6})/) || [null, String(t || "").trim()])[1].toUpperCase().replace(/[^A-Z0-9]/g, "").slice(-6);

function LinkTv({ sid, initial }) {
  const [v, setV] = useState("");
  const [scan, setScan] = useState(false);
  const [ok, setOk] = useState(false);
  const link = (c) => {
    const code = pairCode(c);
    if (code.length !== 6) return toast.error("Kodi i TV-së ka 6 shkronja");
    api.post(`/tv/sessions/${sid}/link`, { code }).then(() => { setOk(true); setScan(false); toast.success("U lidh me TV"); }).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => { if (initial) link(initial); }, [initial]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="bg-emerald-50 rounded-2xl p-3 space-y-2" data-testid="tv-link-box">
      <div className="text-xs font-bold text-emerald-800">{ok ? "✓ TV u lidh me kod" : "Kodi që shfaqet në TV (gjejshqip.com/tv)"}</div>
      <div className="flex gap-2">
        <input data-testid="tv-link-input" value={v} maxLength={6} onChange={(e) => setV(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))} placeholder="ABC123" className="flex-1 min-w-0 w-0 h-11 rounded-xl bg-white border border-emerald-200 px-2 text-lg font-black tracking-[0.15em] text-center outline-none" />
        <button data-testid="tv-link-go" onClick={() => link(v)} className="btn-3d-green h-11 px-4 rounded-xl font-bold">Lidhu</button>
      </div>
      <button data-testid="tv-link-scan" onClick={() => setScan(!scan)} className="btn-3d-soft w-full h-11 rounded-xl font-bold flex items-center justify-center gap-2 text-slate-800"><QrCode className="w-4 h-4" />{scan ? "Mbyll kamerën" : "Skano QR-në e TV-së"}</button>
      {scan && <QrCamera onCode={link} />}
    </div>
  );
}

// Chromecast via Presentation API (Chrome on Android / desktop)
function castTo(code) {
  try {
    const req = new window.PresentationRequest([tvUrl(code)]);
    req.start().then(() => toast.success("U lidh me TV")).catch((e) => { if (e?.name !== "AbortError") toast.error("Nuk u gjet asnjë TV me Chromecast. Përdor kodin më poshtë."); });
  } catch { toast.error("Ky shfletues nuk mbështet Chromecast. Përdor kodin më poshtë."); }
}

function Controls({ s, set, mine }) {
  const live = s.seen_at && Date.now() - new Date(s.seen_at).getTime() < 9000;
  const cur = mine.find((m) => m.id === s.listing_id);
  return (
    <div className="space-y-3">
      <div data-testid="tv-status" className={`text-sm font-bold rounded-xl px-3 py-2 text-center ${live ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{live ? "TV është i lidhur" : "Duke pritur televizorin..."}</div>
      {mine.length > 1 && (
        <select data-testid="tv-switch-listing" value={s.listing_id} onChange={(e) => set({ listing_id: e.target.value })} className="w-full h-11 rounded-xl bg-slate-50 border border-slate-200 px-3 text-sm">
          {!cur && <option value={s.listing_id}>Reklama aktuale</option>}
          {mine.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
        </select>
      )}
      {cur && cur.wheels.length > 1 && (
        <div className="flex flex-wrap gap-1.5">{cur.wheels.map((w, i) => <button key={i} data-testid={`tv-wheel-${i}`} onClick={() => set({ wheel: i })} className={`h-9 px-3 rounded-full text-xs font-bold ${s.wheel === i ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-700"}`}>{w || `Rrota ${i + 1}`}</button>)}</div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <button data-testid="tv-play-toggle" onClick={() => set({ playing: !s.playing })} className="btn-3d-soft h-12 rounded-xl font-bold flex items-center justify-center gap-2 text-slate-800">{s.playing ? <><Pause className="w-4 h-4" />Ndalo</> : <><Play className="w-4 h-4" />Vazhdo</>}</button>
        <button data-testid="tv-close" onClick={() => set({ closed: true })} className="h-12 rounded-xl font-bold flex items-center justify-center gap-2 bg-rose-50 text-rose-600"><X className="w-4 h-4" />Mbyll</button>
      </div>
    </div>
  );
}

function Connect({ code }) {
  const canCast = typeof window !== "undefined" && "PresentationRequest" in window;
  return (
    <div className="space-y-3">
      {canCast && <button data-testid="tv-chromecast" onClick={() => castTo(code)} className="btn-3d-green w-full h-12 rounded-xl font-bold flex items-center justify-center gap-2"><Cast className="w-5 h-5" />Kërko TV (Chromecast)</button>}
      <div className="bg-slate-50 rounded-2xl p-3 text-center">
        <div className="text-xs text-slate-500 mb-1">Ose në shfletuesin e TV-së hap <b>{window.location.host}/tv</b>, shtyp "Kam kod nga telefoni" dhe shkruaj:</div>
        <div data-testid="tv-code" className="text-3xl font-black tracking-[0.2em] text-slate-900">{code}</div>
        <div className="flex justify-center mt-2"><QRCodeSVG value={tvUrl(code)} size={120} /></div>
        <div className="text-[11px] text-slate-400 mt-1">Ose skano QR-në me TV-në ose me kompjuterin e lidhur me TV</div>
      </div>
      <details className="text-xs text-slate-600 bg-white border border-slate-100 rounded-xl p-3" data-testid="tv-help">
        <summary className="font-bold cursor-pointer">Nuk e gjen televizorin?</summary>
        <ul className="list-disc pl-4 mt-2 space-y-1">
          <li>Telefoni dhe TV duhet të jenë në të njëjtin Wi-Fi.</li>
          <li>Chromecast / Google TV: përdor Chrome në Android dhe shtyp "Kërko TV".</li>
          <li>iPhone + Apple TV / TV me AirPlay: hap "Pamja TV në telefon", pastaj Qendra e Kontrollit → Pasqyrimi i ekranit (Screen Mirroring).</li>
          <li>Smart TV pa Cast (Samsung, LG...): hap shfletuesin e TV-së dhe shkruaj kodin.</li>
        </ul>
        <a href={tvUrl(code)} target="_blank" rel="noopener noreferrer" data-testid="tv-open-local" className="mt-2 inline-flex items-center gap-1 font-bold text-emerald-700"><Smartphone className="w-3.5 h-3.5" />Pamja TV në telefon</a>
      </details>
    </div>
  );
}

export function CastDialog({ listingId, onClose, tvCode }) {
  const [s, setS] = useState(null);
  const [mine, setMine] = useState([]);
  useEffect(() => {
    api.post("/tv/sessions", { listing_id: listingId }).then((r) => setS(r.data)).catch((e) => { toast.error(errMsg(e)); onClose(); });
    api.get("/tv/my-listings").then((r) => setMine(r.data)).catch(() => {});
  }, [listingId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!s?.id || s.closed) return undefined;
    const id = setInterval(() => api.get(`/tv/sessions/${s.id}`).then((r) => setS(r.data)).catch(() => {}), 3000);
    return () => clearInterval(id);
  }, [s?.id, s?.closed]);
  const set = (b) => api.put(`/tv/sessions/${s.id}`, b).then((r) => { setS(r.data); if (b.closed) { toast.success("Transmetimi u mbyll"); onClose(); } }).catch((e) => toast.error(errMsg(e)));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm w-[calc(100vw-1.5rem)] rounded-3xl max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="tv-cast-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Tv className="w-5 h-5 text-emerald-600" />Shfaq në TV</DialogTitle></DialogHeader>
        {!s ? <div className="h-40 animate-pulse bg-slate-100 rounded-2xl" /> : <div className="space-y-4 min-w-0"><Controls s={s} set={set} mine={mine} /><LinkTv sid={s.id} initial={tvCode} /><Connect code={s.code} /></div>}
      </DialogContent>
    </Dialog>
  );
}

export function TvCastButton({ listingId, className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" data-testid={`tv-cast-btn-${listingId}`} onClick={(e) => { e.stopPropagation(); setOpen(true); }} aria-label="Shfaq në TV" title="Shfaq në TV"
        className={className || "w-10 h-10 shrink-0 flex items-center justify-center text-slate-900 active:scale-90 transition-transform"}>
        <Cast className="w-7 h-7" strokeWidth={2.4} />
      </button>
      {open && <CastDialog listingId={listingId} onClose={() => setOpen(false)} />}
    </>
  );
}
